import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave, fnGetTableById } from "@shared/api/restApi";
import { CalAm, getInitFormData, CalAmBulk } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import { getNoSlip } from "@shared/utils/CommonUtils";
import { global, useMenuStore } from "@shared/libs/Global";
import SN_QMS_REQ_REG_001_POP_01 from "@menu/QMS/SN_QMS_REQ_REG_001/SN_QMS_REQ_REG_001_POP_01";
import SnResizeBar from "@shared/components/SnResizeBar";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

const SN_QMS_REQ_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const { menuItems } = useMenuStore();
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedLineDocu, setSelectedLineDocu] = useState<number>(0);
  const configRef = useRef<any[]>([]);
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [selectedTab, setSelectedTab] = useState("1");
  const [focusedRowData, setFocusedRowData] = useState<any>();

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));
  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);
  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
        const payload = {
          id: "config",
          params: { cd_firm: userInfo.cd_firm, cd_ctrl: "QMS0006" },
        };
        const data = await fnGetTableById(payload);
        if (Array.isArray(data) && data.length > 0) {
          configRef.current = data;
        }
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved = await onSave();
        if (!isSaved) return;
      }
    }

    const selectedFormData = searchRef.current.getFormdata();
    if (selectedFormData.dt_from > selectedFormData.dt_to) {
      await showDialog(
        "alter",
        " 조회기간의 종료일이 시작일보다빠릅니다. 확인 후 다시 조회해주시기 바랍니다."
      );
      return;
    }

    try {
      const params = {
        id: "sn_qms_req_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      setSelectedDocu("");
      setFocusedRowData(0);

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const searchRefData = searchRef.current.getFormdata();

      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_biz_isu: userInfo?.cd_biz,
        fg_insp: "1",
        cd_emp_lot: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_dept_lot: userInfo?.cd_dept,
        st_qc: searchRefData.st_qc,
        cd_gp: userInfo?.cd_gp,
        dt_lot: today,
        yn_confirm: "N",
        fg_level: configRef.current[0].dc_ctrl,
        cd_aql: "0",
        yn_autolot: "Y",
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const selectedNoLot = focusedNodeH?.data.no_lot;
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line_lot");
      const newRow = {
        cd_firm: userInfo.cd_firm,
        no_lot: selectedNoLot ?? null,
        yn_lot: "Y",
        no_line_lot: maxNoLine + 1,
        st_qc: "F",
      };
      return gridRefs.current["grid2"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid3"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const headerChanges = gridRefs.current["grid1"].current.getChange();
    if (!headerChanges.delete || headerChanges.delete.length === 0) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      return;
    }
    return await onSave();
  };

  const onSave = async () => {
    let lothChanges;
    let reqChanges;
    let lotCerthChanges;

    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();
      gridRefs.current["grid3"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      lothChanges = gridRefs.current["grid1"].current.getChange();
      reqChanges = gridRefs.current["grid2"].current.getChange();
      if (reqChanges.insert && reqChanges.insert.length > 0) {
        reqChanges.update = [
          ...(reqChanges.update || []),
          ...reqChanges.insert,
        ];
        reqChanges.insert = [];
      }

      // 🔥 modifiedRowcount 재계산 (중요)
      reqChanges.modifiedRowcount =
        (reqChanges.update?.length || 0) + (reqChanges.delete?.length || 0);
      lotCerthChanges = gridRefs.current["grid3"].current.getChange();
      if (reqChanges.delete.length > 0) {
        const deleteRows = reqChanges.delete;
        const convertedUpdates = deleteRows.map((row: any) => ({
          ...row,
          st_qc: "P",
          no_lot: null,
          yn_lot: "N",
          no_line_lot: 0,
        }));

        reqChanges.update = [...(reqChanges.update ?? []), ...convertedUpdates];

        reqChanges.delete = [];
      }
      const headerInserts = lothChanges.insert ?? [];
      const lineInserts = reqChanges.update ?? [];
      const lineInserts2 = lotCerthChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog("alter", "저장에 실패했습니다.");
        return false;
      }
      if (
        lothChanges.modifiedRowcount === 0 &&
        reqChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      for (let row of headerInserts) {
        if (!row.no_lot) {
          const cd_module: any = Menuprops.cd_module;
          const cd_menu: any = Menuprops.cd_menu;
          try {
            // getNoSlip 호출
            const no_lot = await getNoSlip(
              cd_module,
              1, // no_line 값이 필요한 경우 사용
              "QMS", // cd_prefix 값이 필요하다면 여기에 적어주세요.
              row.dt_lot, // 검사LOT일자 기준 채번 (dt_io는 헤더에 없어 0001년으로 채번됨)
              cd_menu // cd_user 값
            );
            if (no_lot) {
              row.no_lot = no_lot;
              [...(reqChanges.update ?? [])].forEach((line: any) => {
                if (!line.no_lot) {
                  line.no_lot = no_lot;
                }
              });
              for (let row of lineInserts2) {
                if (!row.no_lot) {
                  if (no_lot) {
                    row.no_lot = no_lot;
                  }
                }
              }
              const focusedNodeH =
                gridRefs.current["grid1"].current.focusedRowNode();
              if (focusedNodeH) focusedNodeH.setDataValue("no_lot", no_lot);

              gridRefs.current["grid2"].current.applyFilters({
                no_lot: no_lot,
              });
              gridRefs.current["grid3"].current.applyFilters({
                no_lot: no_lot,
              });
            }
          } catch (error) {}
        }
      }

      const saveData = {
        loth: lothChanges,
        req: reqChanges,
        lot_certh: lotCerthChanges,
      };
      const ok = await fnSave("SN_QMS_REQ_REG_001_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid3"].current.resetUpdate(true);
      return false;
    }
  };

  const onBtnsClick = async (id: string) => {
    if (id === "aButtonApply") {
      const purPohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (purPohChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }
      if (gridRefs.current["grid2"].current?.getDisplayedRowCount() > 0) {
        await showDialog("alter", "이미 의뢰를 적용하셨습니다.");
        return;
      }
      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_item: purPohChanges[0].cd_item ?? "",
      };

      const component = (
        <SN_QMS_REQ_REG_001_POP_01 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "의뢰적용",
        children: component,
        validation: false,
        width: "69%",
      });
      if (!result || result === "close") return;
      const headerResult = result[0];
      const lineResult = result[1];
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNodeH && headerResult[0]) {
        for (const key in headerResult[0]) {
          focusedRowNodeH.setDataValue(key, headerResult[0][key], "noEvent");
        }
        focusedRowNodeH.setDataValue("cd_item", headerResult[0].cd_item ?? "");
        focusedRowNodeH.setDataValue(
          "fg_grade",
          headerResult[0].fg_grade ?? ""
        );
        focusedRowNodeH.setDataValue(
          "cd_aql",
          headerResult[0].cd_aql ?? "",
          "noEvent"
        );
      }
      let qt_lot = 0;
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              addedNode.setDataValue(key, result[key], "noEvent");
              if (key === "qt_req") qt_lot += result[key];
            }
            focusedRowNodeH.setDataValue("qt_lot", qt_lot, "noEvent");
          }
        }
      });
      gridRefs.current["grid2"].current.resetUpdate();
    }
  };

  const onFocusedRowChanged = async (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_lot = selectedRow.no_lot;
      gridRefs.current["grid2"].current.applyFilters({
        no_lot: no_lot,
      });
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      gridRefs.current["grid3"].current.applyFilters({
        no_lot: no_lot,
      });
      gridRefs.current["grid3"].current.selectAndFocusRow(0);
    } else {
      return;
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerData = gridRefs.current["grid2"].current.getFooterData();
    if (colId === "no_lot") {
      // 필터링
      gridRefs.current["grid2"].current.applyFilters({
        no_lot: rowData.no_lot,
      });
      gridRefs.current["grid3"].current.applyFilters({
        no_lot: rowData.no_lot,
      });
    } else if (colId === "cd_item") {
      const payload = {
        id: "sipitem",
        params: { cd_firm: userInfo.cd_firm, cd_item: rowData.cd_item },
      };
      const data = await fnGetTableById(payload);
      if (Array.isArray(data) && data.length > 0) {
        data.forEach((item: any, index: any) => {
          const newRow = {
            cd_firm: userInfo.cd_firm,
            no_line: index + 1,
            cd_clause: item.cd_clause,
            nm_clause: item.nm_clause,
            dc_unit_cl: item.dc_unit_cl,
            dc_ucl: item.dc_ucl,
            dc_lcl: item.dc_lcl,
            yn_sample: item.yn_sample,
            qt_sample: item.yn_sample === "Y" ? null : focusedNodeH.data.qt_lot,
            yn_confirm: "N",
            yn_insp: item.yn_insp || "N",
          };
          gridRefs.current["grid3"].current.addRow(newRow);
        });
      }
    } else if (
      colId === "fg_level" ||
      colId === "fg_grade" ||
      colId === "cd_aql"
    ) {
      const payload = {
        id: "ksa",
        params: {
          cd_firm: userInfo.cd_firm,
          fg_level: rowData.fg_level,
          fg_grade: rowData.fg_grade,
          cd_aql: rowData.cd_aql,
          qt_lot: rowData.qt_lot,
        },
      };
      const data = await fnGetTableById(payload);
      if (Array.isArray(data) && data.length > 0) {
        const ksa = data[0];
        gridRefs.current["grid3"].current.forEachFilteredNode(
          (node: RowNode) => {
            if (node.data.yn_sample === "Y") {
              node.setDataValue("qt_sample", ksa.qt_sample);
              node.setDataValue("qt_ac", ksa.qt_ac);
              node.setDataValue("qt_re", ksa.qt_re);
            } else {
              node.setDataValue(
                "qt_sample",
                focusedNodeH.data.qt_lot,
                "noEvent"
              );
            }
          }
        );
      }
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
  };

  const onCellValueChangedD = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
  };
  // SN_QMS_REQ_REG_001.tsx
  const onActivate = async (nodeData: any) => {
    try {
      if (!nodeData) return;

      const header = nodeData.header;
      const lines = nodeData.lines;
      let qt_lot = 0;
      lines.forEach((row: any) => {
        qt_lot += row.qt_req;
      });
      if (!header) return;

      // 호출 화면(검사대기현황)에서 넘어온 의뢰의 검사구분.
      // qms_req PK 구성요소라 임의로 덮어쓰면 저장 시 의뢰 행을 못 찾는다.
      const fgInsp = lines?.[0]?.fg_insp ?? header.fg_insp;

      // 🔥 2. grid 세팅
      const grid1 = gridRefs.current["grid1"].current;
      const grid2 = gridRefs.current["grid2"].current;
      // 기존 데이터 초기화
      const today = dateToString(new Date());
      // 상단 1건
      const addedNode = grid1.addRow({
        ...header,
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_biz_isu: userInfo?.cd_biz,
        fg_insp: fgInsp,
        cd_emp_lot: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_dept_lot: userInfo?.cd_dept,
        st_qc: "F",
        cd_gp: userInfo?.cd_gp,
        dt_lot: today,
        qt_lot: qt_lot,
        yn_confirm: "N",
        cd_aql: "0",
        yn_autolot: "Y",
      });

      const payload = {
        id: "sipitem",
        params: { cd_firm: userInfo.cd_firm, cd_item: header.cd_item },
      };
      const data = await fnGetTableById(payload);

      if (Array.isArray(data) && data.length > 0) {
        data.forEach((item: any, index: any) => {
          const newRow = {
            cd_firm: userInfo.cd_firm,
            no_line: index + 1,
            cd_clause: item.cd_clause,
            nm_clause: item.nm_clause,
            dc_unit_cl: item.dc_unit_cl,
            dc_ucl: item.dc_ucl,
            dc_lcl: item.dc_lcl,
            qt_sample: item.yn_sample === "N" ? qt_lot : null,
            yn_confirm: "N",
            yn_insp: item.yn_insp  || "N",
          };
          gridRefs.current["grid3"].current.addRow(newRow);
        });
      }

      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line_lot");

      // 하단 여러건
      if (lines && lines.length > 0) {
        lines.forEach((row: any, idx: number) => {
          const newRow = {
            ...row,
            cd_firm: userInfo.cd_firm,
            yn_lot: "Y",
            no_line_lot: maxNoLine + 1,
            st_qc: "F",
          };
          grid2.addRow(newRow);
        });
      }
      setTimeout(() => {
        grid1.selectAndFocusRow(0);
        if (lines?.length > 0) {
          grid2.selectAndFocusRow(0);
        }
      }, 0);
    } catch (error: any) {
      console.error(error);
    }
  };
  const onTabChanged = (index: string) => {
    setSelectedTab(index);

    if (focusedRowData) {
      const no_lot = focusedRowData.no_lot;
      const gridMap: { [key: string]: React.RefObject<any> } = {
        "1": gridRefs.current["grid2"],
        "2": gridRefs.current["grid3"],
      };

      const currentGrid = gridMap[index];
      if (currentGrid && currentGrid.current) {
        currentGrid.current.applyFilters({ no_lot });
        gridRefs.current["grid2"].current.selectAndFocusRow(0);
        gridRefs.current["grid3"].current.applyFilters({ no_lot });
        gridRefs.current["grid3"].current.selectAndFocusRow(0);
      }
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onCellValueChanged: onCellValueChangedD,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "의뢰정보",
            }}
            events={grids.grid1.events}
            header={[
              <SnTabHeader
                body={
                  <div className="relative flex space-x-2">
                    <SnButton
                      text={"의뢰적용"}
                      onClick={() => {
                        onBtnsClick("aButtonApply");
                      }}
                    />
                  </div>
                }
              />,
            ]}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="49.5%">
          <SnTab
            titles={["검사의뢰", "검사항목"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            body={[
              <div className="h-full">
                <SnGrid
                  ref={gridRefs.current["grid2"]}
                  columnConfig={grids.grid2.columns}
                  rowData={grids.grid2.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                  }}
                  events={grids.grid2.events}
                />
              </div>,
              <div className="h-full">
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={grids.grid3.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                  }}
                  events={grids.grid3.events}
                />
              </div>,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={selectedLineDocu}
        />
      </div>
    </div>
  );
});

export default SN_QMS_REQ_REG_001;
